import { Service } from '@/modules/Service/types'

export interface CustomerService {
    service_id: number
    service_name: string
    product_group_name: string
    service_status: string
    customer_id: number
    service_start_date: Date
    service_stopped_date: Date
}
export interface TicketRequest {
    ticket_request_id: number
    ticket_request_title: string
    ticket_request_key: string
}
export interface TicketType {
    ticket_type_id: number
    ticket_type_title: string
    ticket_type_key: string
    ticket_type_sample_content: string
    ticket_type_time: string
    ticket_type_time_format: string
    ticket_request_id: number
    ticket_priority: string
    ticket_type_group?: string
}

export type TicketTypeGroup =
    | 'support_request'
    | 'error_service'
    | 'service_request'
    | 'service'
    | 'meeting'
    | 'change'
    | 'internal'
export type NotificationStatus = 'pending' | 'sent' | 'failed' | null

export interface Ticket {
    ticket_id: number
    created_by_name: string
    company_name: string
    ticket_status: string
    ticket_priority: string
    ticket_type_id: number
    ticket_type_title: string
    ticket_type_key: string
    ticket_type_group?: string
    ticket_request_id: number
    ticket_request_title: string[]
    ticket_source: string
    ticket_content: string
    ticket_note: string
    ticket_product_name: string[]
    ticket_customer_feedback: string
    ticket_requested_at: string
    ticket_customer_rating: string
    ticket_deadline_at: Date
    employee_id: number
    ticket_technical_assignees: number[]
    ticket_technical_assignee_names: { employee_name: string }[]
    ticket_cs_assignees: number[]
    ticket_cs_assignee_names: { employee_name: string }[]
    ticket_sale_assignees: number[]
    ticket_sale_assignee_names: { employee_name: string }[]
    ticket_employee_assignees: number[]
    ticket_department: number
    ticket_solution: string
    ticket_estimated_completion_date: string
    ticket_resolved_at: Date
    ticket_meeting_type: string
    ticket_meeting_via: string
    ticket_meeting_channel: string
    customer_id: number
    customer_name: string
    service_id: number
    service_startdate: Date
    service_duedate: Date
    employee_name: string
    service_name: string
    service_status: string
    product_group_name: string[]
    ticket_time_repply: string
    ticket_time_response: string
    ticket_time_release: string
    ticket_attachment?: File[]
    attachment?: TicketAttachment
    ticket_response_late: boolean
    ticket_owner_id: number
    ticket_owner_type: string
    ticket_root_cause: string
    ticket_remedial_measures: string
    ticket_avoid: string
    ticket_affected: string
    ticket_incident_type: string
    ticket_security_tag: TicketSecurityTag
    ticket_marjorable: boolean[]
    ticket_internal_root_cause: string
    ticket_internal_remedial_measures: string
    ticket_internal_avoid: string
    ticket_groups: string[]
    ticket_scope: string
    created_at: string
    updated_at: string
    notification_status?: NotificationStatus
}

export interface TicketAttachment {
    ticket_attactment_name: string
    ticket_attachment_path: string
    ticket_attachment_type: string
    ticket_attachment_size: number
    ticket_id: number
}
export interface TicketLogs {
    ticket_log_id: number
    ticket_log_action: string
    ticket_id: number
    ticket_log_change: Metadata | TicketCreatedMetadata | null
    created_at: string
    updated_at: string
    employee_id: number
    employee_name: string
    ticket_log_content: string
    ticket_log_title: string
}

export interface TicketAttachments {
    ticket_attactment_id: number
    ticket_attactment_name: string
    ticket_attachment_path: string
    ticket_attachment_type: string
    ticket_attachment_size: number
    ticket_id: number
    employee_id: number
    employee_name: string
    created_at: Date
    updated_at: Date
}
export interface Metadata {
    new: {
        [key: string]: string | any[]
    }
    old: {
        [key: string]: string | any[]
    }
}
export interface TicketCreatedMetadata {
    action: string
    ticket_id: number
}

export type TicketStatusType =
    | 'opening'
    | 'work-in-progress'
    | 'escalated'
    | 'closed'
    | 'completed'

// IT-ISMS: Security classification tag per QT-01 §4.2
export type TicketSecurityTag = 'none' | 'security' | 'service' | 'both'

// IT-ISMS: Priority mapping P1-P4 per QT-01 §4.2
export type TicketISMSPriority = 'P1' | 'P2' | 'P3' | 'P4'

/**
 * Maps DataCentral 3-level priority + marjorable flag to IT-ISMS P1-P4.
 * high + marjorable = P1 (Critical), high = P2 (High), medium = P3 (Medium), low = P4 (Low)
 */
export const mapPriorityToISMS = (
    priority: string,
    marjorable?: boolean[] | boolean
): TicketISMSPriority => {
    const isMajor = Array.isArray(marjorable)
        ? marjorable[0] === true
        : marjorable === true
    switch (priority) {
        case 'high':
            return isMajor ? 'P1' : 'P2'
        case 'medium':
            return 'P3'
        case 'low':
        default:
            return 'P4'
    }
}

export interface TicketStatusSummary {
    ticket_status: TicketStatusType
    total_tickets: number
}
export type TicketPriority = 'low' | 'medium' | 'high'

export interface TicketAvgPriority {
    avg_resolution_time: number
    ticket_priority: TicketPriority
    total_tickets: number
    resolved_tickets: number
    pending_tickets: number
}
export interface TicketHandler {
    employee_id: number
    employee_name: string
    total_tickets: number
    resolved_tickets: number
}
export interface OverdueTicketsByEmployee {
    employee_id: number
    employee_name: string
    total_tickets: number
    ontime_tickets: number
    overdue_tickets: number
    overdue_tickets_closed: number
    overdue_tickets_closed_on_time: number
}

export interface TicketStatusPeriod {
    period: string
    open_tickets: number
    closed_tickets: number
}
// export type TicketStatusPeriod = Record<
//     string,
//     {
//         opened: number
//         closed: number
//     }
// >

export interface TicketCustomerService extends Partial<Service> {
    cs_assignees?: number[]
    sale_assignees?: number[]
}

export interface TicketCustomerServiceInteresting {
    label: string
    value: string
    cs_assignees?: number[]
    sale_assignees?: number[]
}

export interface TicketCustomerServices {
    services: TicketCustomerService[]
    service_interestings: TicketCustomerServiceInteresting[]
}

// Statistics types - Distribution
export interface TicketTypeDetail {
    ticket_type_id: number
    ticket_type_title: string
    ticket_type_key: string
    total_tickets: number
    percentage: number
}

export interface TicketsByType {
    ticket_type_group: string
    group_total: number
    group_percentage: number
    ticket_types: TicketTypeDetail[]
}

export interface TicketsBySource {
    ticket_source: string
    total_tickets: number
    percentage: number
}

export interface TicketsByProduct {
    product_group_name: string
    total_tickets: number
    percentage: number
}

export interface TopCustomerByTickets {
    customer_id: number
    customer_name: string
    company_name: string
    total_tickets: number
}

export interface MeetingTicketsByPurpose {
    ticket_meeting_purpose: string
    total_tickets: number
    percentage: number
}

// Statistics types - Performance
export interface AssigneeWorkload {
    employee_id: number
    employee_name: string
    employee_email: string
    open_tickets: number
    high_priority: number
    medium_priority: number
    low_priority: number
    overdue: number
}

export interface CustomerSatisfaction {
    total_rated: number
    avg_rating: number
    five_star: number
    four_star: number
    three_star: number
    two_star: number
    one_star: number
}

export interface SLACompliance {
    total_tickets: number
    ontime_count: number
    late_count: number
    pending_count: number
    ontime_percentage: number
}

export interface AvgFirstResponseTime {
    avg_first_response_hours: number
    by_priority: {
        priority: string
        avg_hours: number
    }[]
}

export interface TicketResolutionRate {
    total_tickets: number
    resolved_tickets: number
    resolution_rate: number
    by_type: {
        ticket_type_title: string
        total: number
        resolved: number
        rate: number
    }[]
}

export interface MajorIncidentsSummary {
    total_major_incidents: number
    resolved: number
    in_progress: number
    avg_resolution_time_hours: number
    by_product: {
        product_group_name: string
        count: number
    }[]
}

// Statistics types - Trend
export interface TicketCreationTrend {
    period: string
    total_created: number
    by_type: {
        ticket_type_title: string
        count: number
    }[]
}

// Feedback types
export interface FeedbackToken {
    token_id: number
    ticket_id: number
    token: string
    status: string
    expires_at: string
    created_at: string
    created_by: number
    recipient_name?: string
    recipient_email?: string
    recipient_phone?: string
    employee_name?: string
}

export interface FeedbackTokenResponse {
    token_id: number
    token: string
    expires_at: string
    feedback_url: string
    recipient_name?: string
}

export interface TicketFeedback {
    feedback_id: number
    ticket_id: number
    token_id?: number
    /** Trung bình cộng của rating_support_quality và rating_staff_attitude */
    rating: number
    /** null với các đánh giá cũ, tạo trước khi tách 2 tiêu chí */
    rating_support_quality?: number | null
    rating_staff_attitude?: number | null
    feedback_text?: string
    feedback_by_name?: string
    feedback_by_email?: string
    feedback_by_phone?: string
    created_at: string
}

export interface FeedbackStats {
    average_rating: number
    total_feedbacks: number
}

export interface TicketForFeedback {
    ticket_id: number
    ticket_status: string
    ticket_content: string
    ticket_priority: string
    ticket_requested_at: string
    ticket_created_at: string
    ticket_resolved_at?: string
    customer_name: string
    service_name: string
    ticket_request_title: string
    ticket_type_title: string
    employee_name?: string
    recipient_name?: string
    token_status: string
    expires_at: string
    already_submitted: boolean
}

// Tickets by employee and type group
export interface TicketCountByTypeGroup {
    ticket_type_group: TicketTypeGroup
    total_tickets: number
}

export interface TicketsByEmployeeAndType {
    employee_id: number
    employee_name: string
    ticket_counts: TicketCountByTypeGroup[]
    total_tickets: number
    // Tickets that have ever been escalated, not tickets currently in the
    // escalated status. Optional so an older API build reads as 0 instead of
    // rendering NaN.
    escalated_tickets?: number
}

// One row of the escalated-tickets drill-down. Mirrors the API exactly:
// escalated_at and escalated_by_id are nullable columns, and escalated_by_name
// comes back as '' rather than null when the log has no author.
export interface EscalatedTicketItem {
    ticket_id: number
    ticket_content: string
    ticket_type_title: string
    ticket_status: string
    // No UTC offset, like every other timestamp in this API — render through
    // formatFromUTC(), never with new Date() directly.
    ticket_requested_at: string
    escalated_at: string | null
    escalated_by_id: number | null
    escalated_by_name: string
    reason: string
}
